import fetchUpdateHelpfulReviewUpvotes from "./fetchUpdateHelpfulReviewUpvotes";
import Notification from "~/common/Notification";
import {
  FilteredBy,
  ProductReviewDetail,
  SavedHelpfulUserVotes,
} from "./reviewModels";

export const allVariations = "all_options";
export const allRatings = "all";

export const getTotalRatings = (productReviewDetails: ProductReviewDetail[]) =>
  productReviewDetails.filter((review) => review.oneToFiveStarRating > 0);

export const getShortenedReviewerName = (fullName: string) => {
  let fullNameArray = fullName
    .trim()
    .replace("-", "")
    .replace(/\s+/g, " ")
    .split(" ");
  fullNameArray = fullNameArray?.map(
    (x) => x.charAt(0).toUpperCase() + x.substring(1).toLowerCase(),
  );

  if (!fullNameArray) {
    return fullName;
  }

  const shortenedReviewerName =
    fullNameArray?.length > 1
      ? `${fullNameArray[0]} ${fullNameArray[1]!.slice(0, 1)}.`
      : fullNameArray[0];

  return shortenedReviewerName;
};

export const updateHelpfulReview = async (
  reviewId: number,
  isHelpful: boolean,
) => {
  if (reviewId > 0) {
    try {
      await fetchUpdateHelpfulReviewUpvotes(reviewId, isHelpful);
    } catch (error) {
      new Notification(
        {
          heading: `Unable to update customer review as ${
            isHelpful ? "helpful" : "unhelpful"
          }`,
          body: "Please try again. <a href='/ask.html'>Chat</a> with us if you're still having issues.",
        },
        "error",
      );
    }
  }
};

export const sortReviews = (
  reviewsToSort: ProductReviewDetail[],
  sortBy: string,
) => {
  const reviews = reviewsToSort;
  switch (sortBy) {
    case "highest_rated":
      reviews.sort((a, b) => b.oneToFiveStarRating - a.oneToFiveStarRating);
      break;
    case "lowest_rated":
      reviews.sort((a, b) => a.oneToFiveStarRating - b.oneToFiveStarRating);
      break;
    case "date":
      reviews.sort(
        (a, b) =>
          new Date(b.textReview.dateSubmitted).getTime() -
          new Date(a.textReview.dateSubmitted).getTime(),
      );
      break;
    default:
      reviews.sort((a, b) => b.upvote - a.upvote);
      break;
  }
  return reviews;
};

export const filterReviews = (
  reviews: ProductReviewDetail[],
  filterBy: FilteredBy,
) => {
  if (
    filterBy.variation.value === allVariations &&
    filterBy.rating === allRatings
  ) {
    return reviews;
  }

  if (filterBy.variation.value === allVariations) {
    return reviews.filter(
      (r) => r.oneToFiveStarRating === Number(filterBy.rating),
    );
  }

  if (filterBy.rating === allRatings) {
    return reviews.filter((review) =>
      review.variations?.some(
        (variation) =>
          variation.type === filterBy.variation.type &&
          variation.value === filterBy.variation.value,
      ),
    );
  }

  return reviews.filter(
    (review) =>
      review.variations?.some(
        (variation) =>
          variation.type === filterBy.variation.type &&
          variation.value === filterBy.variation.value,
      ) && review.oneToFiveStarRating === Number(filterBy.rating),
  );
};

export const recordHelpfulVoteOnStorage = (
  reviewId: number,
  userIndex: number,
  upvoted: boolean,
) => {
  const userVotes: SavedHelpfulUserVotes[] = JSON.parse(
    localStorage.getItem("helpfulUserVotes") || "[]",
  );

  if (
    !userVotes.find(
      (vote) => vote.reviewId === reviewId && vote.userIndex === userIndex,
    )
  ) {
    const newUserVotes = [...userVotes, { reviewId, userIndex, upvoted }];

    localStorage.setItem("helpfulUserVotes", JSON.stringify(newUserVotes));
  }
};

export const getUserExistingHelpfulVotesFromStorage = (userIndex: number) => {
  let userVotes: SavedHelpfulUserVotes[] = JSON.parse(
    localStorage.getItem("helpfulUserVotes") || "[]",
  );

  userVotes = userVotes.filter((vote) => vote.userIndex === userIndex);

  const userVotesMap: Record<number, boolean> = {};

  userVotes.forEach((vote) => {
    userVotesMap[vote.reviewId] = vote.upvoted;
  });

  return userVotesMap;
};

export const convertBooleanToInt = (bool: boolean) => {
  return Number(bool);
};
